import { GenericRepository, PagedList } from '@/repository/GenericRepository';
import { get as apiGet, post as apiPost } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { DocumentType } from '@aws-amplify/core/internals/utils';

export interface JWT {
  toString(): string;
}

export interface CognitoUser {
  Username: string;
  Attributes?: Array<{
    Name: string;
    Value: string;
  }>;
}

export interface CognitoGroup {
  GroupName: string;
}

export interface CognitoUserPagedList extends PagedList<
  CognitoUser,
  'UserPagedList'
> {
  __typename: 'UserPagedList';
}

interface AdminQueryListUsersResponse {
  Users?: CognitoUser[];
}

/**
 * Utility type for making typed requests.
 * Modify if your endpoints return different shapes.
 */
type HttpMethod = 'GET' | 'POST';

export class CognitoUserRepository extends GenericRepository<CognitoUser> {
  /**
   * Searches (lists) Cognito users via Admin Queries
   */
  public override async search(): Promise<CognitoUserPagedList> {
    const data = await this.doRequest<AdminQueryListUsersResponse>(
      'GET',
      '/listUsersWithGroups'
    );

    const users = data?.Users || [];
    return {
      __typename: 'UserPagedList',
      items: users,
      total: users.length,
    };
  }

  /**
   * Add user to group using Admin Queries
   */
  public async addUserToGroup(username: string, groupName: string) {
    const data = await this.doRequest(
      'POST',
      '/addUserToGroup',
      { username, groupname: groupName },
      `Failed to add user [${username}] to group [${groupName}]`
    );
    if (!data) {
      throw new Error('Empty response from addUserToGroup API call.');
    }
    return data;
  }

  /**
   * Remove user from group
   */
  public async removeUserFromGroup(username: string, groupName: string) {
    const data = await this.doRequest(
      'POST',
      '/removeUserFromGroup',
      { username, groupname: groupName },
      `Failed to remove user [${username}] from group [${groupName}]`
    );
    if (!data) {
      throw new Error('Empty response from removeUserFromGroup API call.');
    }
    return data;
  }

  /**
   * List all groups
   */
  public async listGroups(username?: string) {
    // If we wanted to list groups for a specific user, we'd need a different endpoint.
    if (username) {
      throw new Error(
        `'listGroups(username)' not implemented for Admin Queries.`
      );
    }

    // Example response shape: { Groups?: CognitoGroup[] }
    const data = await this.doRequest<{ Groups?: CognitoGroup[] }>(
      'GET',
      '/listGroups'
    );
    if (!data?.Groups) {
      throw new Error(`Failed to get groups: response missing 'Groups' field.`);
    }
    return data.Groups;
  }

  // ---------------------------------------------------------------------------
  // Private utility methods
  // ---------------------------------------------------------------------------

  /**
   * Consolidates getting the token, building headers,
   * and calling the appropriate Amplify API method.
   *
   * @param method   - 'GET' or 'POST'
   * @param path     - API path (e.g., '/listUsersWithGroups')
   * @param body     - Request body if POST
   * @param errMsg   - Optional custom error prefix for better context
   */
  private async doRequest<T>(
    method: HttpMethod,
    path: string,
    body?: DocumentType | FormData,
    errMsg?: string
  ): Promise<T> {
    try {
      const token = await this.getTokenOrThrow();

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: token.toString(),
      };

      const operation =
        method === 'GET'
          ? apiGet({
              apiName: 'AdminQueries',
              path,
              options: { headers },
            })
          : apiPost({
              apiName: 'AdminQueries',
              path,
              options: { headers, body },
            });

      const response = await operation.response;
      return (await response.body.json()) as T;
    } catch (err) {
      // If an optional custom error prefix is given, append the original error.
      const prefix = errMsg ? `${errMsg}: ` : '';
      throw new Error(`${prefix}${String(err)}`);
    }
  }

  /**
   * Utility to get a valid token or throw.
   */
  private async getTokenOrThrow(): Promise<JWT> {
    try {
      const authSession = await fetchAuthSession();
      const token = authSession?.tokens?.accessToken;
      if (!token) {
        throw new Error('No valid token found in auth session.');
      }
      return token;
    } catch (error) {
      throw new Error(`Error fetching auth session: ${String(error)}`);
    }
  }
}
